/**
 * The two reads behind the top bar's `‹ Poprzednia / Następna ›` pair. Decides nothing —
 * `model/clientNeighbours` owns the answer.
 *
 * ── It is the ROSTER's two reads, not two of its own ──────────────────────────────────────────
 * `rosterQueryOptions` and `attentionQueueQueryOptions` are `entities/client`'s, and they are the
 * same options the roster screen mounts. Arriving here from the roster therefore costs no request
 * at all: the cache is already warm and the arrows resolve on the first frame. Arriving from a
 * pasted link costs the two reads once, which is the price of the address meaning anything.
 *
 * The attention read is folded in because the DEFAULT ordering is "wymagający uwagi najpierw", and
 * `buildClientRoster` is what puts a risk reason on a row. `?? null` collapses in-flight and failed
 * into one thing, exactly as the roster screen does: no risk lines, the roster regardless. The
 * arrows then walk a list ordered by name inside one band rather than refusing — a degradation the
 * trainer can see the shape of, since the two screens degrade identically.
 */

import { useQueries } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  attentionQueueQueryOptions,
  buildClientRoster,
  rosterQueryOptions,
  type RosterContext,
} from '~/entities/client';

import { clientNeighbours, type ClientNeighbours } from '../model/clientNeighbours';

export function useClientNeighbours(clientId: number, context: RosterContext): ClientNeighbours {
  const { i18n } = useTranslation();
  const [roster, attention] = useQueries({
    queries: [rosterQueryOptions(), attentionQueueQueryOptions()],
  });

  const rows = roster.isSuccess
    ? buildClientRoster(roster.data, attention.data ?? null).rows
    : null;

  return clientNeighbours(rows, clientId, {
    ...context,
    locale: i18n.language,
    // Read here rather than passed in: the only rule that consults it is the "Nieaktywni" tab's
    // 14-day lapse, and a day-boundary crossing mid-session moves at most one client between two
    // adjacent positions on one list.
    now: new Date(),
  });
}
